import React, { useEffect, useState } from 'react';
import styles from './ResourceCollaboration.module.scss';
import { Autocomplete } from 'new/elements/Autocomplete';
import { Button } from 'new/elements/Button';
import cx from 'classnames';
import { AutocompleteRenderGroupParams, MenuItem, Typography } from '@mui/material';
import { useOrgMembers } from 'new/hooks/adapters/useOrgMembers';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { User } from 'new/entities/User';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useTeams } from 'new/hooks/adapters/useTeams';
import { useOrgs } from 'new/hooks/adapters/useOrgs';
import { Select } from 'new/elements/Select';
import { useDispatch } from 'react-redux';
import { updateMultipleAcl } from 'js/actions';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { UPDATE_ACL_REQUEST } from 'js/constants/actionTypes';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { ACL_RESOURCE_TYPE_DATASET, ACL_RESOURCE_TYPE_DF_DATASET } from 'js/constants/aclConstants';
import { TextField } from 'new/elements/TextField';

interface ShareActionProps {
  data: any;
  aclType?: string;
  resourceId: string;
  alreadyShared?: User[];
  externalSelection?: User[];
}

const ACL_SETTINGS_MAPPING = {
  owner: {
    key: 'owner',
    title: 'Owner',
    value: 'owner',
    description:
      'Resource creator/owner. This user has full administrative rights to this resource. Contact support@nexla.com for transfer of ownership.',
  },
  collaborator: {
    key: 'collaborator',
    title: 'Collaborator (Read-only)',
    value: 'collaborator',
    description:
      'Able to view all information about this resource and use it in other Flows or Nexsets, but should not be able to modify it.',
  },
  admin: {
    key: 'admin',
    title: 'Administrator',
    value: 'admin',
    description: 'Full administrative rights to this resource.',
  },
};

const ACL_ASSIGNMENT_OPTIONS = [
  ACL_SETTINGS_MAPPING['collaborator'],
  ACL_SETTINGS_MAPPING['admin'],
];

const AutocompleteOption = (props, option) => {
  const caption = (() => {
    if (option.optype === 'USER') return option.full_name;
    if (option.optype === 'ORG') return option.name;
    if (option.optype === 'TEAM') return option.name;
  })();
  const body3 = (() => {
    if (option.optype === 'USER') return option.email;
    if (option.optype === 'ORG') return `Everyone in ${option.name} organization`;
    if (option.optype === 'TEAM') return option.description;
  })();
  return (
    <div
      {...props}
      className={cx(
        styles.autocompleteOptionContainer,
        props['aria-selected'] ? styles.autocompleteOptionContainer_selected : '',
      )}
    >
      <Typography variant="caption">{caption}</Typography>
      <Typography variant="body3">{body3}</Typography>
    </div>
  );
};

export const ShareAction = (props: ShareActionProps) => {
  const { data, alreadyShared, aclType, resourceId, externalSelection } = props;

  const share = [ACL_RESOURCE_TYPE_DF_DATASET, ACL_RESOURCE_TYPE_DATASET].includes(aclType);

  const [role, setRole] = useState(!share ? 'collaborator' : 'sharer');
  const [items, setItems] = useState([]);
  const [selecteds, setSelecteds] = useState([]);

  const dispatch = useDispatch();

  const loading = useIsLoading(UPDATE_ACL_REQUEST);

  const org = useCurrentOrg();
  const user = useCurrentUser();
  const members = useOrgMembers({ onlyValid: true, orgId: org.id });
  const orgs = useOrgs();
  const teams = useTeams();

  useEffect(() => {
    setItems([
      ...members
        .filter(
          (i) =>
            !alreadyShared.some((a) => a.id === i.id) &&
            i.id !== user.id &&
            i.email !== 'admin@nexla.com',
        )
        .map((m) => ({ ...m, label: m.full_name, optype: 'USER' })),
      ...teams
        .filter((t) => !alreadyShared.some((a) => a.id === t.id))
        .map((t) => ({ ...t, label: t.name, optype: 'TEAM' })),
      ...orgs
        .filter((i) => {
          if (!share) {
            return i.id === org.id;
          }
          return !alreadyShared.some((a) => a.id === i.id);
        })
        .map((o) => ({ ...o, label: o.name, optype: 'ORG' })),
    ]);
  }, [members, orgs, teams]);

  const onChangeSelection = (e, data) => {
    setSelecteds(data);
  };

  const renderGroup = ({ group, children }: AutocompleteRenderGroupParams) => {
    return (
      <div>
        <p className={styles.autocompleteGroup}>{group}</p>
        {children}
      </div>
    );
  };

  const renderSelectItem = (item) => {
    return (
      <MenuItem className={styles.selectOption} value={item.value}>
        <Typography>{item.title}</Typography>
        <Typography variant="caption">{item.description}</Typography>
      </MenuItem>
    );
  };

  const onChangeRole = (e) => {
    setRole(e.target.value);
  };

  const onAdd = () => {
    dispatch(updateMultipleAcl(aclType, data, selecteds, role));
  };

  const onChangeAccess = () => {
    dispatch(updateMultipleAcl(aclType, data, externalSelection, role));
  };
  const onDelete = () => {
    dispatch(updateMultipleAcl(aclType, data, externalSelection));
  };

  if (loading) {
    return <CenteredLoader minHeight={85} />;
  }

  return (
    <div className={styles.shareActions}>
      {!externalSelection || !externalSelection.length ? (
        <Autocomplete
          multiple
          size="small"
          sx={{
            width: 300,
          }}
          groupBy={(option) => option.optype}
          renderGroup={renderGroup}
          getOptionLabel={(option) => {
            if (option.optype === 'USER') return option.full_name;
            return option.name;
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disableClearable
          onChange={onChangeSelection}
          renderOption={AutocompleteOption}
          renderInput={(params) => (
            <TextField {...params} variant="outlined" placeholder="Search users, teams or orgs" />
          )}
          options={items.map((m) => ({ ...m, label: m.full_name }))}
        />
      ) : null}
      {!share && (
        <Select
          size="small"
          renderItem={renderSelectItem}
          value={role}
          renderValue={(value: string) => {
            return ACL_SETTINGS_MAPPING[value]?.title;
          }}
          sx={{ minWidth: 200 }}
          items={ACL_ASSIGNMENT_OPTIONS}
          onChange={onChangeRole}
        />
      )}
      {!externalSelection || !externalSelection.length ? (
        <Button onClick={onAdd} disabled={!selecteds.length} variant="contained">
          {share ? 'Share' : 'Grant Access'}
        </Button>
      ) : (
        <>
          {!share ? (
            <Button onClick={onChangeAccess} variant="outlined">
              Change Access
            </Button>
          ) : null}
          <Button onClick={onDelete} variant="contained" color="error">
            Remove
          </Button>
        </>
      )}
    </div>
  );
};

ShareAction.defaultProps = {
  alreadyShared: [],
};
